/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Settings — planning preferences, eaters, meal slots, pantry staples.
 *
 * Every list on this page used to be read-only: you could see that someone was
 * allergic to peanuts, that dinner was a meal slot, that salt was a staple —
 * and change none of it. A screen that shows you data you cannot edit isn't
 * helping anyone do anything, so each section now owns its full add / edit /
 * remove.
 *
 * Household membership lives on the Family page now, not here.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { CirclesApi, PantryApi, SettingsApi } from "../../api/endpoints.ts";
import type { DietProfile } from "../../api/types.ts";
import type { ModelSettings } from "../../api/chef.ts";
import { useToday } from "../../lib/useToday.tsx";
import { Banner, ErrorBox, Loading, PageHeader, Pill } from "../../components/ui.tsx";
import { useToast } from "../../components/Toast.tsx";
import { usePrefs } from "../../i18n/index.tsx";
import { DietProfileEditor } from "./DietProfileEditor.tsx";
import { MedicalEditor } from "./MedicalEditor.tsx";
import { DisplaysCard } from "./DisplaysCard.tsx";

export function SettingsView() {
  const { t } = usePrefs();
  const qc = useQueryClient();
  const toast = useToast();
  const { refresh } = useToday();

  const [editingDiet, setEditingDiet] = useState<DietProfile | null>(null);
  const [addingDiet, setAddingDiet] = useState(false);
  const [newStaple, setNewStaple] = useState("");
  const [newSlotLabel, setNewSlotLabel] = useState("");
  const [newSlotKey, setNewSlotKey] = useState("");

  const settings = useQuery({ queryKey: ["settings"], queryFn: () => SettingsApi.get() });
  const staples = useQuery({ queryKey: ["staples"], queryFn: () => PantryApi.staples() });
  const circlesSettings = useQuery({
    queryKey: ["circles-settings"],
    queryFn: () => CirclesApi.settings.get(),
  });
  const suggestions = useQuery({
    queryKey: ["staple-suggestions"], queryFn: () => PantryApi.suggestions(),
  });

  const refreshSettings = () => {
    void qc.invalidateQueries({ queryKey: ["settings"] });
    void qc.invalidateQueries({ queryKey: ["home"] });
  };
  const refreshStaples = () => {
    void qc.invalidateQueries({ queryKey: ["staples"] });
    void qc.invalidateQueries({ queryKey: ["staple-suggestions"] });
  };
  const onErr = (e: Error) => toast(e.message, "danger");

  const refreshCircles = () => {
    void qc.invalidateQueries({ queryKey: ["circles-settings"] });
    void qc.invalidateQueries({ queryKey: ["circles"] });
  };

  // Only fetched when circles are on — a disabled household has no groups and
  // no picker to populate.
  const myCircles = useQuery({
    queryKey: ["circles"],
    queryFn: () => CirclesApi.list(),
    enabled: circlesSettings.data?.enabled === true,
    retry: false,
  });

  const setPinned = useMutation({
    mutationFn: (circleId: string | null) => CirclesApi.settings.setPinned(circleId),
    onSuccess: () => {
      refreshCircles();
      // The Today pane reads this out of the home payload, which is now stale.
      void qc.invalidateQueries({ queryKey: ["home"] });
      toast("Saved", "ok");
    },
    onError: onErr,
  });

  const setAutoAccept = useMutation({
    mutationFn: (enabled: boolean) => CirclesApi.settings.setAutoAccept(enabled),
    onSuccess: () => {
      refreshCircles();
      // Turning this on back-fills every circle you are already in, so the
      // recipe list on screen is now stale.
      void qc.invalidateQueries({ queryKey: ["recipes"] });
    },
    onError: onErr,
  });

  const setCirclesEnabled = useMutation({
    mutationFn: (enabled: boolean) => CirclesApi.settings.set(enabled),
    onSuccess: (data) => {
      refreshCircles();
      const enabled = data.enabled;
      toast(
        `Circles ${enabled ? "enabled" : "disabled"} — you ${enabled ? "can now" : "will no longer"} create and join chat rooms.`,
        "ok",
      );
    },
    onError: onErr,
  });

  const update = useMutation({
    mutationFn: (body: Record<string, unknown>) => SettingsApi.update(body),
    onSuccess: () => {
      refreshSettings();
      // The timezone or week start may have changed, which moves every date the
      // UI shows — re-ask the server rather than guessing locally.
      refresh();
      void qc.invalidateQueries({ queryKey: ["plan"] });
      toast("Saved", "ok");
    },
    onError: onErr,
  });

  const removeDiet = useMutation({
    mutationFn: (id: string) => SettingsApi.removeDiet(id),
    onSuccess: () => { refreshSettings(); toast("Removed", "ok"); },
    onError: onErr,
  });

  const addSlot = useMutation({
    mutationFn: () => SettingsApi.addSlot({
      key: newSlotKey.trim().toLowerCase(), label: newSlotLabel.trim(),
    }),
    onSuccess: () => {
      setNewSlotKey(""); setNewSlotLabel(""); refreshSettings(); toast("Slot added", "ok");
    },
    onError: onErr,
  });
  const removeSlot = useMutation({
    mutationFn: (id: string) => SettingsApi.removeSlot(id),
    onSuccess: () => { refreshSettings(); toast("Removed", "ok"); },
    onError: onErr,
  });

  const addStaple = useMutation({
    mutationFn: (displayName: string) => PantryApi.addStaple({ displayName, alwaysHave: true }),
    onSuccess: () => { setNewStaple(""); refreshStaples(); toast("Added", "ok"); },
    onError: onErr,
  });
  const removeStaple = useMutation({
    mutationFn: (id: string) => PantryApi.removeStaple(id),
    onSuccess: () => { refreshStaples(); toast("Removed", "ok"); },
    onError: onErr,
  });

  if (settings.isLoading) return <Loading />;
  if (settings.error) return <ErrorBox message={(settings.error as Error).message} />;
  if (!settings.data) return null;

  const s = settings.data.settings;
  const diets = settings.data.diets;
  const slots = settings.data.slots;
  const stapleList = staples.data?.staples ?? [];
  const have = new Set(stapleList.map((x) => x.display_name.toLowerCase()));
  const unusedSuggestions = (suggestions.data?.suggestions ?? [])
    .filter((x) => !have.has(x.display_name.toLowerCase()));
  const circleList = myCircles.data?.circles ?? [];
  const pinnedId = circlesSettings.data?.pinnedCircleId ?? null;

  return (
    <div className="max-w-3xl">
      <PageHeader
        title="Settings"
        subtitle="How the planner sizes portions, names meals, and what it leaves off your list"
      />

      {/* ── Who's eating ─────────────────────────────────────────────── */}
      <section className="card mb-6 p-5">
        <div className="mb-1 flex items-baseline justify-between gap-3">
          <h2 className="font-medium">Who's eating</h2>
          <button className="btn-primary text-xs" onClick={() => setAddingDiet(true)}>
            Add someone
          </button>
        </div>
        <p className="mb-3 text-sm text-ink-3">
          Allergens listed here are a hard constraint: generated recipes and
          suggested substitutions are checked against them in code, and planned
          meals that conflict are flagged.
        </p>

        {diets.length === 0 ? (
          <p className="text-sm text-ink-3">
            Nobody added yet — the planner can't size portions or check allergies
            without this.
          </p>
        ) : (
          <ul className="divide-y divide-rule-soft">
            {diets.map((d) => (
              <li key={d.id} className="flex items-start justify-between gap-3 py-3">
                <div className="min-w-0">
                  <span className="font-medium text-ink">{d.name}</span>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {d.allergens.map((a) => <Pill key={a} tone="danger">{a}</Pill>)}
                    {d.diet_tags.map((t) => <Pill key={t} tone="neutral">{t}</Pill>)}
                    {d.allergens.length === 0 && d.diet_tags.length === 0 && (
                      <span className="text-xs text-ink-3">No restrictions</span>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="text-xs text-ink-3">{d.counts_as_servings}× portion</span>
                  <button className="btn-ghost !px-2 !py-1 text-xs"
                          onClick={() => setEditingDiet(d)}>Edit</button>
                  <button className="px-1.5 text-ink-3 hover:text-danger"
                          title={`Remove ${d.name}`}
                          onClick={() => removeDiet.mutate(d.id)}>✕</button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-3">
          <Banner tone="warn">
            Ingredient tagging is best-effort. Always read labels — don't rely on
            this app for a severe allergy.
          </Banner>
        </div>
      </section>

      {/* ── Planning ─────────────────────────────────────────────────── */}
      <section className="card mb-6 space-y-3 p-5">
        <h2 className="font-medium">Planning</h2>

        <label className="block">
          <span className="text-sm">Timezone</span>
          <input className="input mt-1" defaultValue={s.timezone}
                 onBlur={(e) => {
                   if (e.target.value !== s.timezone) update.mutate({ timezone: e.target.value });
                 }} />
          {/* This is the anchor for every date in the app, so a wrong value is
              rejected rather than silently defaulted. */}
          <p className="mt-1 text-xs text-ink-3">
            An IANA name like America/New_York. Everything the app calls "today"
            is resolved in this zone.
          </p>
        </label>
        {update.error && <ErrorBox message={(update.error as Error).message} />}

        <div className="grid gap-3 sm:grid-cols-3">
          <label className="block">
            <span className="text-sm">Week starts on</span>
            <select className="input mt-1" value={s.week_starts_on}
                    onChange={(e) => update.mutate({ weekStartsOn: Number(e.target.value) })}>
              <option value={1}>Monday</option>
              <option value={7}>Sunday</option>
            </select>
          </label>

          <label className="block">
            <span className="text-sm">Measurements</span>
            <select className="input mt-1" value={s.measurement_system}
                    onChange={(e) => update.mutate({ measurementSystem: e.target.value })}>
              <option value="us">US (cups, oz, lb)</option>
              <option value="metric">Metric (g, ml)</option>
            </select>
          </label>

          <label className="block">
            <span className="text-sm">Default servings</span>
            <input type="number" min={1} className="input mt-1" defaultValue={s.default_servings}
                   onBlur={(e) => update.mutate({ defaultServings: Number(e.target.value) })} />
          </label>
        </div>
      </section>

      {/* ── Medical considerations ────────────────────────────────────── */}
      <section className="card mb-6 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-medium">Medical considerations</h2>
            <p className="mt-1 text-sm text-ink-3">
              Record conditions or medications that change how someone should eat,
              and the app will factor them into suggestions and flag the things
              worth knowing. Off by default.
            </p>
          </div>
          <label className="flex shrink-0 items-center gap-2">
            <span className="text-sm">
              {s.medical_considerations_enabled ? "On" : "Off"}
            </span>
            <input
              type="checkbox"
              className="h-5 w-5 rounded border-rule bg-paper-2 text-accent focus:ring-accent"
              checked={!!s.medical_considerations_enabled}
              onChange={(e) =>
                update.mutate({ medicalConsiderationsEnabled: e.target.checked })}
              disabled={update.isPending}
            />
          </label>
        </div>

        {/* Said before it is switched on, not after. This is health data and
            the whole household can see it — that is a decision, not a detail. */}
        {!s.medical_considerations_enabled && (
          <p className="mt-3 rounded-lg border border-rule-soft bg-paper-2 p-3 text-xs text-ink-3">
            This stores health information, which everyone in your household can
            see — the same as diet profiles and allergies. It's used to shape
            suggestions and warnings, and it is never medical advice. You can
            switch it off at any time, which hides everything immediately.
          </p>
        )}

        {s.medical_considerations_enabled && (
          <div className="mt-4 border-t border-rule-soft pt-4">
            <MedicalEditor />
          </div>
        )}
      </section>

      {/* ── Monsieur Croissant ────────────────────────────────────────── */}
      {settings.data?.models && (
        <section className="card mb-6 space-y-4 p-5">
          <div>
            <h2 className="font-medium">Monsieur Croissant</h2>
            <p className="mt-1 text-sm text-ink-3">
              Which models the assistant uses. Both lists come from the server,
              so they stay in step with what your account can actually run.
            </p>
          </div>

          <ModelPicker
            label="Recipe writing"
            hint="Used when the assistant writes a new recipe, or reads one off a web page."
            group={settings.data.models.recipe}
            onChange={(id) => update.mutate({ recipeModel: id })}
            onReset={() => update.mutate({ recipeModel: null })}
            disabled={update.isPending}
          />

          <ModelPicker
            label="Conversation"
            hint="Used for every message. This one runs constantly, so it's the one that drives cost."
            group={settings.data.models.chat}
            onChange={(id) => update.mutate({ chatModel: id })}
            onReset={() => update.mutate({ chatModel: null })}
            disabled={update.isPending}
          />

          {/* Stated rather than implied: a family sharing a plan should know the
              assistant can reach outside the app. */}
          <p className="border-t border-rule-soft pt-3 text-xs text-ink-3">
            {settings.data.models.webSearchAvailable
              ? "Monsieur Croissant can search the web for things that change — prices, " +
                "recalls, what's in season, where to buy something. He'll show you " +
                "his sources whenever he does, and he won't search for anything he " +
                "already knows."
              : "Web search is switched off on this server, so the assistant answers " +
                "only from your own data and what it already knows."}
          </p>
        </section>
      )}

      {/* ── Circles chat ──────────────────────────────────────────────── */}
      {circlesSettings.data && (
        <section className="card mb-6 p-5">
          <div className="flex items-baseline justify-between gap-3">
            <div>
              <h2 className="font-medium">{t("settings.circles")}</h2>
              <p className="mt-1 text-sm text-ink-3">{t("settings.circlesHint")}</p>
            </div>
            <label className="flex items-center gap-2">
              <span className="text-sm">
                {circlesSettings.data.enabled ? t("settings.circlesDisable") : t("settings.circlesEnable")}
              </span>
              <input
                type="checkbox"
                className="h-5 w-5 rounded border-rule bg-paper-2 text-accent focus:ring-accent"
                checked={circlesSettings.data.enabled}
                onChange={(e) => setCirclesEnabled.mutate(e.target.checked)}
                disabled={setCirclesEnabled.isPending}
              />
            </label>
          </div>
          {/* Only meaningful once circles are on, and deliberately a separate
              decision: this one lets other households write into your library. */}
          {circlesSettings.data.enabled && (
            <div className="mt-4 border-t border-rule-soft pt-4">
              <div className="flex items-baseline justify-between gap-3">
                <div>
                  <h3 className="text-sm font-medium">{t("settings.autoAccept")}</h3>
                  <p className="mt-1 text-sm text-ink-3">{t("settings.autoAcceptHint")}</p>
                </div>
                <input
                  type="checkbox"
                  className="h-5 w-5 shrink-0 rounded border-rule bg-paper-2 text-accent focus:ring-accent"
                  checked={circlesSettings.data.autoAcceptRecipes}
                  onChange={(e) => setAutoAccept.mutate(e.target.checked)}
                  disabled={setAutoAccept.isPending}
                  aria-label={t("settings.autoAccept")}
                />
              </div>
              {setAutoAccept.error && (
                <ErrorBox message={(setAutoAccept.error as Error).message} />
              )}
            </div>
          )}

          {/* ── which group shows on Today ─────────────────────────────────
              Only worth asking once there is more than one group: for a
              household in a single group the automatic answer is already
              right, and a picker with one option is a question with no
              content. */}
          {circlesSettings.data.enabled && circleList.length > 1 && (
            <div className="mt-4 border-t border-rule-soft pt-4">
              <h3 className="text-sm font-medium">Show on Today</h3>
              <p className="mt-1 text-sm text-ink-3">
                Which group's latest posts appear on your Today screen.
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                <button
                  type="button"
                  className="chip-toggle"
                  aria-pressed={!pinnedId}
                  onClick={() => setPinned.mutate(null)}
                  disabled={setPinned.isPending}
                >
                  Most active
                </button>
                {circleList.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className="chip-toggle"
                    aria-pressed={pinnedId === c.id}
                    onClick={() => setPinned.mutate(c.id)}
                    disabled={setPinned.isPending}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
              {setPinned.error && (
                <ErrorBox message={(setPinned.error as Error).message} />
              )}
            </div>
          )}

          {setCirclesEnabled.error && (
            <ErrorBox message={(setCirclesEnabled.error as Error).message} />
          )}
        </section>
      )}

      {/* ── Meal slots ───────────────────────────────────────────────── */}
      <section className="card mb-6 p-5">
        <h2 className="font-medium">Meal slots</h2>
        <p className="mb-3 text-sm text-ink-3">
          Breakfast, lunch, dinner — or whatever your days actually look like.
        </p>

        <ul className="divide-y divide-rule-soft">
          {slots.map((slot) => (
            <li key={slot.id} className="flex items-center justify-between gap-3 py-2">
              <span className="text-sm">
                {slot.label}
                <span className="ml-2 font-mono text-xs text-ink-3">{slot.key}</span>
              </span>
              <button className="px-1.5 text-ink-3 hover:text-danger"
                      title={`Remove ${slot.label}`}
                      onClick={() => removeSlot.mutate(slot.id)}>✕</button>
            </li>
          ))}
        </ul>

        <div className="mt-3 flex gap-2">
          <input className="input" placeholder="Label, e.g. Snack"
                 value={newSlotLabel} onChange={(e) => setNewSlotLabel(e.target.value)} />
          <input className="input font-mono" placeholder="key"
                 value={newSlotKey}
                 onChange={(e) => setNewSlotKey(e.target.value.toLowerCase().replace(/\s+/g, "_"))} />
          <button className="btn-ghost shrink-0"
                  disabled={!newSlotLabel.trim() || !newSlotKey.trim() || addSlot.isPending}
                  onClick={() => addSlot.mutate()}>Add</button>
        </div>
      </section>

      {/* ── Raspberry Pi display ─────────────────────────────────────── */}
      <DisplaysCard />

      {/* ── Pantry staples ───────────────────────────────────────────── */}
      <section className="card mb-6 p-5">
        <h2 className="font-medium">Always in the cupboard</h2>
        <p className="mb-3 text-sm text-ink-3">
          These are left off generated shopping lists.
        </p>

        {staples.isLoading ? <Loading /> : (
          <div className="flex flex-wrap gap-1.5">
            {stapleList.map((x) => (
              <span key={x.id} className="chip gap-1.5">
                {x.display_name}
                <button className="text-ink-3 hover:text-danger"
                        title={`Remove ${x.display_name}`}
                        onClick={() => removeStaple.mutate(x.id)}>✕</button>
              </span>
            ))}
            {stapleList.length === 0 && <span className="text-sm text-ink-3">None yet.</span>}
          </div>
        )}

        <div className="mt-3 flex gap-2">
          <input className="input" placeholder="Salt, olive oil, flour…"
                 value={newStaple}
                 onChange={(e) => setNewStaple(e.target.value)}
                 onKeyDown={(e) => {
                   if (e.key === "Enter" && newStaple.trim()) addStaple.mutate(newStaple.trim());
                 }} />
          <button className="btn-ghost shrink-0"
                  disabled={!newStaple.trim() || addStaple.isPending}
                  onClick={() => addStaple.mutate(newStaple.trim())}>Add</button>
        </div>

        {unusedSuggestions.length > 0 && (
          <div className="mt-4 border-t border-rule-soft pt-3">
            <div className="label-mono mb-2">You buy these often</div>
            <div className="flex flex-wrap gap-1.5">
              {unusedSuggestions.map((sug) => (
                <button key={sug.food_id} className="chip hover:border-accent"
                        onClick={() => addStaple.mutate(sug.display_name)}>
                  + {sug.display_name}
                  <span className="ml-1 text-ink-3">{sug.uses}×</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      <p className="text-sm text-ink-3">
        Members, invites and joining a household are on the{" "}
        <Link to="/family" className="text-accent hover:underline">Family</Link> page.
      </p>

      {addingDiet && (
        <DietProfileEditor onClose={() => setAddingDiet(false)} onSaved={refreshSettings} />
      )}
      {editingDiet && (
        <DietProfileEditor existing={editingDiet}
                           onClose={() => setEditingDiet(null)}
                           onSaved={refreshSettings} />
      )}
    </div>
  );
}

/**
 * One model choice, rendered from the server's own description of it.
 *
 * Radio buttons rather than a <select>: there are two or three options, each
 * needs a sentence explaining what changes about your dinner, and a dropdown
 * hides exactly that sentence until after you have chosen.
 */
function ModelPicker({ label, hint, group, onChange, onReset, disabled }: {
  label: string;
  hint: string;
  group: ModelSettings["recipe"];
  onChange: (id: string) => void;
  onReset: () => void;
  disabled?: boolean;
}) {
  return (
    <fieldset className="border-t border-rule-soft pt-3" disabled={disabled}>
      <legend className="sr-only">{label}</legend>
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <p className="text-sm font-medium">{label}</p>
          <p className="text-xs text-ink-3">{hint}</p>
        </div>
        {/* Clearing is a real choice, not a no-op: it means "follow the
            server default", so this household keeps moving when that moves. */}
        {!group.isDefault && (
          <button type="button" className="btn-ghost shrink-0 px-2 py-1 text-xs"
                  onClick={onReset}>
            Use default
          </button>
        )}
      </div>

      <div className="mt-2 space-y-1.5">
        {group.choices.map((m) => (
          <label
            key={m.id}
            className={`flex cursor-pointer gap-2.5 rounded-lg border p-2.5 transition ${
              group.selected === m.id
                ? "border-accent/50 bg-accent-soft"
                : "border-rule-soft hover:bg-paper-3"}`}
          >
            <input
              type="radio"
              name={label}
              className="mt-0.5 h-4 w-4 shrink-0 accent-accent"
              checked={group.selected === m.id}
              onChange={() => onChange(m.id)}
            />
            <span className="min-w-0">
              <span className="block text-sm">
                {m.label}
                {m.isDefault && (
                  <span className="ml-1.5 text-[11px] text-ink-3">default</span>
                )}
              </span>
              <span className="block text-xs text-ink-3">{m.blurb}</span>
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
